import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import Navbar from '@/components/feature/Navbar'
import Footer from '@/components/feature/Footer'
import ScrollToTop from '@/components/base/ScrollToTop'
import Breadcrumb from '@/components/base/Breadcrumb'
import { getArticleById, getHotArticles, Article } from '@/mocks/articles'

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>()
  const [article, setArticle] = useState<Article | undefined>(undefined)
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  useEffect(() => {
    if (!id) return
    const found = getArticleById(id)
    setArticle(found)
    if (found) {
      const sameCategory = getHotArticles(10).filter(
        (a) => a.category === found.category && a.id !== found.id
      ).slice(0, 3)
      setRelatedArticles(sameCategory.length > 0 ? sameCategory : getHotArticles(3).filter((a) => a.id !== found.id))
    }
  }, [id])

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-800 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">您访问的文章可能已被移除或不存在。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    )
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    image: article.coverImage,
    url: `https://tomleeled.cn/article/${article.id}`,
    inLanguage: 'zh-CN',
    articleSection: article.categoryName,
    isPartOf: { '@type': 'WebSite', name: '色里番精选推荐', url: 'https://tomleeled.cn/' },
  }

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="pt-20">
        <article className="max-w-[900px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                {article.categoryName}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-500">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views.toLocaleString()} 次浏览
              </span>
              <span className="flex items-center gap-1 text-accent-500">
                <i className="ri-star-fill"></i>
                {article.rating} 分
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-auto max-h-[500px] object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none mb-16 article-content text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200 pt-8 mb-16">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500 font-medium">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500"></i>
                          {ra.rating}
                        </span>
                        <span>{ra.publishDate}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  )
}